import { faEye } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useDebounce } from "@/hooks/useDebounce";
import { api, type AuditLogEntry, type OrganizationSummary } from "@/lib/api";
import {
  AUDIT_LOG_PAGE_SIZE,
  FILTROS_PADRAO,
  consultaDosFiltros,
  filtrosDaUrl,
  formatarDataHora,
  linkDoQueFoiVisto,
  rotuloDaAcao,
  rotuloDoTipo,
  urlDosFiltros,
  type AuditLogFilters,
} from "@/lib/auditLog";

//: A mesma grade no cabeçalho e nas linhas.
const COLUNAS = "8.5rem minmax(8rem,1fr) minmax(10rem,1.2fr) 6rem minmax(14rem,2.4fr) 6.5rem minmax(9rem,1.2fr)";

//: A grade da aba «Organizações».
const COLUNAS_DAS_EMPRESAS = "minmax(10rem,1.3fr) minmax(11rem,1.4fr) 5rem 5rem 8rem 8rem 4.5rem 8.5rem";

const PLANOS: Record<string, string> = { starter: "Starter", pro: "Pro", enterprise: "Enterprise" };

/**
 * A aba «Organizações» (21/10/2026): as empresas que o LinkFlow mandou nos
 * links, com o id (a chave) e o nome legível (o último que chegou).
 */
function Organizacoes({ itens, carregando }: { itens: OrganizationSummary[]; carregando: boolean }) {
  const [copiado, setCopiado] = useState<string | null>(null);
  const copiar = (id: string) => {
    void navigator.clipboard?.writeText(id).then(
      () => setCopiado(id),
      () => setCopiado(null),
    );
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <p className="shrink-0 text-xs text-ink-400">
        O nome vem do LinkFlow em cada link: se a empresa mudar de nome lá, muda aqui no próximo link. O <span className="text-ink-200">id</span> é a chave — é por ele que o dicionário e o uso são separados.
      </p>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
        <div className="flex-1 overflow-auto">
          <div className="min-w-[62rem]">
            <div
              className="sticky top-0 grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
              style={{ gridTemplateColumns: COLUNAS_DAS_EMPRESAS }}
            >
              <span>Organização</span>
              <span>ID (do LinkFlow)</span>
              <span>Plano</span>
              <span className="text-right">Links 30 d</span>
              <span>Primeiro link</span>
              <span>Último link</span>
              <span className="text-right">Palavras</span>
              <span />
            </div>
            {carregando && !itens.length ? <div className="py-12 text-center text-sm text-ink-500">Carregando…</div> : null}
            {!carregando && !itens.length ? (
              <div className="py-12 text-center text-sm text-ink-500">
                Nenhuma empresa ainda: elas aparecem quando o LinkFlow manda o cabeçalho de organização no link.
              </div>
            ) : null}
            {itens.map((org) => (
              <div key={org.id} className="grid items-center gap-x-3 border-b border-ink-800 px-3 py-2 text-xs last:border-b-0" style={{ gridTemplateColumns: COLUNAS_DAS_EMPRESAS }}>
                <span className="truncate font-medium text-ink-100" title={org.name ?? undefined}>
                  {org.name || <span className="text-ink-500">sem nome</span>}
                </span>
                <span className="flex min-w-0 items-center gap-2">
                  <code className="truncate font-mono text-[11px] text-ink-300" title={org.id}>
                    {org.id}
                  </code>
                  <button type="button" onClick={() => copiar(org.id)} className={`shrink-0 ${BOTAO} px-2 py-0.5 text-[11px]`}>
                    {copiado === org.id ? "Copiado" : "Copiar"}
                  </button>
                </span>
                <span className="text-ink-300">{org.last_plan ? PLANOS[org.last_plan] ?? org.last_plan : "—"}</span>
                <span className="text-right tabular-nums text-ink-200">{org.links_30d}</span>
                <span className="tabular-nums text-ink-400">{formatarDataHora(org.first_seen_at)}</span>
                <span className="tabular-nums text-ink-400">{formatarDataHora(org.last_seen_at)}</span>
                <span className="text-right tabular-nums text-ink-200">{org.dictionary_accepted + org.dictionary_blocked}</span>
                <span className="flex justify-end gap-3">
                  <Link to={`/uso?org=${encodeURIComponent(org.id)}`} className="text-ink-300 underline underline-offset-2 hover:text-ink-50">
                    uso
                  </Link>
                  <Link to={`/dicionario?org=${encodeURIComponent(org.id)}`} className="text-ink-300 underline underline-offset-2 hover:text-ink-50">
                    dicionário
                  </Link>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
          {itens.length} {itens.length === 1 ? "organização" : "organizações"}
        </div>
      </div>
    </div>
  );
}

const CONTROLE =
  "rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500";

const BOTAO =
  "rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40";

/**
 * O olhinho na frente do arquivo (out/2026): reabre, numa aba nova e no viewer
 * autenticado, a mesma versão — ou o mesmo par — que a pessoa viu. Sem o link no
 * banco não há o que abrir, e o olho fica apagado para a coluna não dançar.
 */
function AtalhoDoQueFoiVisto({ item }: { item: AuditLogEntry }) {
  const url = linkDoQueFoiVisto(item);
  const icone = <FontAwesomeIcon icon={faEye} className="h-3 w-auto" aria-hidden="true" />;
  const caixa = "flex h-4 w-5 shrink-0 items-center justify-center rounded";
  if (!url) {
    return (
      <span className={`${caixa} text-ink-700`} title="O arquivo não está mais no viewer">
        {icone}
      </span>
    );
  }
  const rotulo = item.kind === "compare" ? "Abrir a comparação que a pessoa viu" : "Abrir o arquivo que a pessoa viu";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={rotulo}
      aria-label={rotulo}
      className={`${caixa} text-ink-400 hover:bg-ink-800 hover:text-ink-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-500`}
    >
      {icone}
    </a>
  );
}

/**
 * Auditoria (set/2026): quem abriu e quem comparou arquivos pelo LinkFlow.
 *
 * Só consulta — sem atualização automática, por decisão do dono. Os filtros moram
 * na URL, como na Fila, e a régua (rótulos, período, página) em `lib/auditLog.ts`.
 */
export function Audit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filtros = useMemo(() => filtrosDaUrl(searchParams), [searchParams]);

  const [busca, setBusca] = useState(filtros.q);
  const buscaAdiada = useDebounce(busca, 400);
  const ultimaBuscaAdiada = useRef(buscaAdiada);

  const [itens, setItens] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [aba, setAba] = useState<"aberturas" | "organizacoes">("aberturas");
  const [organizacoes, setOrganizacoes] = useState<OrganizationSummary[]>([]);
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(true);

  // As empresas: o filtro e a aba «Organizações» (21/10/2026).
  useEffect(() => {
    const controle = new AbortController();
    setCarregandoEmpresas(true);
    api
      .listOrganizations(controle.signal)
      .then((resposta) => setOrganizacoes(resposta.items))
      .catch(() => undefined)
      .finally(() => {
        if (!controle.signal.aborted) setCarregandoEmpresas(false);
      });
    return () => controle.abort();
  }, [recarga]);

  const irPara = useCallback(
    (proximos: AuditLogFilters) => setSearchParams(urlDosFiltros(proximos), { replace: true }),
    [setSearchParams],
  );

  // Filtro novo volta para a primeira página; só a paginação muda a página.
  const aplicar = useCallback(
    (mudanca: Partial<AuditLogFilters>) => irPara({ ...filtros, pagina: 1, ...mudanca }),
    [filtros, irPara],
  );

  // A busca digitada vai para a URL depois da pausa — e só quando **ela** mudou,
  // senão o valor atrasado desfaria um «Limpar filtros» recém-clicado.
  useEffect(() => {
    if (ultimaBuscaAdiada.current === buscaAdiada) return;
    ultimaBuscaAdiada.current = buscaAdiada;
    if (buscaAdiada.trim() !== filtros.q) aplicar({ q: buscaAdiada });
  }, [buscaAdiada, filtros.q, aplicar]);

  // A URL mudou por fora (Limpar, voltar do navegador): o campo acompanha, sem
  // comer o espaço que a pessoa acabou de digitar.
  useEffect(() => {
    setBusca((atual) => (atual.trim() === filtros.q ? atual : filtros.q));
  }, [filtros.q]);

  useEffect(() => {
    const controle = new AbortController();
    setCarregando(true);
    setErro(null);
    api
      .listAuditLog(consultaDosFiltros(filtros), controle.signal)
      .then((resposta) => {
        setItens(resposta.items);
        setTotal(resposta.total);
      })
      .catch(() => {
        if (controle.signal.aborted) return;
        setErro("Não foi possível carregar a auditoria.");
      })
      .finally(() => {
        if (!controle.signal.aborted) setCarregando(false);
      });
    return () => controle.abort();
  }, [filtros, recarga]);

  const temFiltro = urlDosFiltros({ ...filtros, pagina: 1 }).toString() !== "";
  const offset = (filtros.pagina - 1) * AUDIT_LOG_PAGE_SIZE;
  const totalPaginas = Math.max(1, Math.ceil(total / AUDIT_LOG_PAGE_SIZE));

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-ink-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-100">Auditoria</h1>
          <p className="mt-0.5 text-xs text-ink-400">
            Quem abriu e quem comparou arquivos pelo LinkFlow.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRecarga((n) => n + 1)}
          disabled={carregando}
          className="rounded border border-ink-700 bg-ink-800 px-3 py-1.5 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:opacity-50"
        >
          ↺ Atualizar
        </button>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-4">
        <div className="flex shrink-0 items-center gap-1 border-b border-ink-800" role="tablist" aria-label="Auditoria">
          {(
            [
              ["aberturas", "Aberturas"],
              ["organizacoes", "Organizações"],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              role="tab"
              aria-selected={aba === valor}
              onClick={() => setAba(valor)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-xs font-medium ${aba === valor ? "border-ink-200 text-ink-100" : "border-transparent text-ink-400 hover:text-ink-200"}`}
            >
              {rotulo}
              {valor === "organizacoes" ? (
                <span className="ml-1 rounded-full bg-ink-800 px-1.5 py-0.5 text-[10px] tabular-nums text-ink-300">{organizacoes.length}</span>
              ) : null}
            </button>
          ))}
        </div>

        {aba === "organizacoes" ? (
          <Organizacoes itens={organizacoes} carregando={carregandoEmpresas} />
        ) : (
        <>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-ink-400">Busca:</span>
          <input
            type="search"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            maxLength={200}
            placeholder="Nome, e-mail, arquivo ou empresa…"
            className={`min-w-[14rem] ${CONTROLE} px-2.5 placeholder-ink-600`}
            aria-label="Buscar por nome, e-mail ou arquivo"
          />

          <span className="text-xs font-medium text-ink-400">Organização:</span>
          <select
            value={filtros.org === null ? "__todas__" : filtros.org}
            onChange={(event) => aplicar({ org: event.target.value === "__todas__" ? null : event.target.value })}
            className={`max-w-[14rem] ${CONTROLE}`}
            aria-label="Filtrar por organização"
          >
            <option value="__todas__">Todas</option>
            {organizacoes.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name || org.id}
              </option>
            ))}
            <option value="">Sem organização</option>
          </select>

          <span className="text-xs font-medium text-ink-400">Ação:</span>
          <select
            value={filtros.acao}
            onChange={(event) => aplicar({ acao: event.target.value as AuditLogFilters["acao"] })}
            className={CONTROLE}
            aria-label="Filtrar por ação"
          >
            <option value="">Todas</option>
            <option value="viewer">Visualizou</option>
            <option value="compare">Comparou</option>
          </select>

          <span className="text-xs font-medium text-ink-400">Tipo:</span>
          <select
            value={filtros.tipo}
            onChange={(event) => aplicar({ tipo: event.target.value as AuditLogFilters["tipo"] })}
            className={CONTROLE}
            aria-label="Filtrar por tipo"
          >
            <option value="">Todos</option>
            <option value="document">Documento</option>
            <option value="packaging">Embalagem</option>
          </select>

          <span className="text-xs font-medium text-ink-400">De:</span>
          <input
            type="date"
            value={filtros.de}
            max={filtros.ate || undefined}
            onChange={(event) => aplicar({ de: event.target.value })}
            className={CONTROLE}
            aria-label="Desde o dia"
          />
          <span className="text-xs font-medium text-ink-400">Até:</span>
          <input
            type="date"
            value={filtros.ate}
            min={filtros.de || undefined}
            onChange={(event) => aplicar({ ate: event.target.value })}
            className={CONTROLE}
            aria-label="Até o dia"
          />

          {temFiltro && (
            <button type="button" onClick={() => irPara(FILTROS_PADRAO)} className={`ml-auto ${BOTAO}`}>
              Limpar filtros
            </button>
          )}
        </div>

        {erro && (
          <p className="flex shrink-0 items-center justify-between rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            <span>{erro}</span>
            <button type="button" onClick={() => setRecarga((n) => n + 1)} className={BOTAO}>
              Tentar de novo
            </button>
          </p>
        )}

        <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
          <div
            className="grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
            style={{ gridTemplateColumns: COLUNAS }}
          >
            <span>Data e hora</span>
            <span>Nome</span>
            <span>E-mail</span>
            <span>Ação</span>
            <span>Arquivo(s)</span>
            <span>Tipo</span>
            <span>Organização</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {carregando && itens.length === 0 && (
              <div className="flex items-center justify-center py-12 text-sm text-ink-500">Carregando…</div>
            )}
            {!carregando && !erro && itens.length === 0 && (
              <div className="flex items-center justify-center py-12 text-sm text-ink-500">
                Nenhum acesso encontrado.
              </div>
            )}
            {itens.map((item) => (
              <div
                key={item.id}
                className="grid items-start gap-x-3 border-b border-ink-800 px-3 py-2.5 text-xs last:border-b-0"
                style={{ gridTemplateColumns: COLUNAS }}
              >
                <span className="tabular-nums text-ink-300">{formatarDataHora(item.created_at)}</span>
                <span className="truncate text-ink-200" title={item.requester_name}>
                  {item.requester_name}
                </span>
                <span className="truncate text-ink-400" title={item.requester_email ?? undefined}>
                  {item.requester_email ?? "—"}
                </span>
                <span className="text-ink-300">{rotuloDaAcao(item.kind)}</span>
                <span className="flex min-w-0 items-start gap-1.5">
                  <AtalhoDoQueFoiVisto item={item} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink-200" title={item.file_path}>
                      {item.file_name}
                    </span>
                    {item.file_b_name && (
                      <span className="block truncate text-ink-200" title={item.file_b_path ?? undefined}>
                        <span className="text-ink-500">× </span>
                        {item.file_b_name}
                      </span>
                    )}
                  </span>
                </span>
                <span>
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[11px] ${
                      item.viewer_kind === "document"
                        ? "border-sky-800 bg-sky-950/40 text-sky-300"
                        : "border-amber-800 bg-amber-950/40 text-amber-300"
                    }`}
                  >
                    {rotuloDoTipo(item.viewer_kind)}
                  </span>
                </span>
                <span className="min-w-0" title={item.organization_id ? `id ${item.organization_id}` : undefined}>
                  {item.organization_id ? (
                    <>
                      <span className="block truncate text-ink-200">{item.organization_name || item.organization_id}</span>
                      {item.organization_name ? (
                        <code className="block truncate font-mono text-[10px] text-ink-500">{item.organization_id}</code>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-ink-600">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
            <span>
              {total > 0
                ? `${Math.min(offset + 1, total)}–${Math.min(offset + itens.length, total)} de ${total}`
                : "0 resultados"}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={filtros.pagina <= 1 || carregando}
                onClick={() => irPara({ ...filtros, pagina: filtros.pagina - 1 })}
                className={BOTAO}
              >
                ← Anterior
              </button>
              <span>
                {filtros.pagina} / {totalPaginas}
              </span>
              <button
                type="button"
                disabled={offset + AUDIT_LOG_PAGE_SIZE >= total || carregando}
                onClick={() => irPara({ ...filtros, pagina: filtros.pagina + 1 })}
                className={BOTAO}
              >
                Próxima →
              </button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </section>
  );
}
